import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import apiUrl from "../Apiurl";
import { BlockSkeleton } from "../interfaccia/loading";
import { alignmentStatusLabel } from "./PrestashopAlignmentPage.helpers";

const DOMAIN_LABELS = {
  TEXT: "Testi",
  FEATURE: "Feature",
  CATEGORY: "Categorie",
  CATEGORY_DEFAULT: "Categoria predefinita",
  IMAGE: "Immagini",
  PRODUCT: "Prodotto",
};

function number(value) {
  return new Intl.NumberFormat("it-IT").format(Number(value || 0));
}

function StatusBreakdown({ values = {} }) {
  const rows = Object.entries(values);
  if (!rows.length) return <span className="scrittaColMedia">Nessun dato</span>;
  return (
    <div className="alignment-kpi-breakdown">
      {rows.map(([status, count]) => (
        <span key={status}>{alignmentStatusLabel(status)} <strong>{number(count)}</strong></span>
      ))}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="alignment-dashboard-skeleton" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, index) => (
        <BlockSkeleton key={index} tone="soft" height={92} />
      ))}
    </div>
  );
}

function requirementLabel(requirement) {
  return requirement.label || requirement.field || requirement.path || "Dato richiesto";
}

function semanticField(path) {
  const value = String(path || "");
  if (value.includes("descIntro")) return "Introduzione Bottero";
  if (value.includes("descBreve")) return "Descrizione breve VIG/partner";
  if (value.includes("descLunga")) return "Descrizione lunga";
  if (value.includes("titolo")) return "Titolo prodotto";
  return value || "--";
}

export default function PrestashopAlignmentDashboard({
  token,
  runId,
  canWrite,
  refreshKey = 0,
  onReanalyzed,
}) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const headers = useMemo(() => ({ Authorization: "Bearer " + token }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(apiUrl + "/moda/prestashop-alignment/dashboard", {
        headers,
        params: runId ? { runId } : {},
      });
      setDashboard(response?.data?.data || null);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
        requestError?.message ||
        "Dashboard allineamento non disponibile.",
      );
    } finally {
      setLoading(false);
    }
  }, [headers, runId, token]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  const reanalyzeBlocked = async () => {
    if (!runId || !canWrite || reanalyzing) return;
    const confirmed = window.confirm(
      "Ricalcolare gli item con blocchi di mapping ancora in revisione? Gli item già approvati o applicati saranno esclusi.",
    );
    if (!confirmed) return;
    setReanalyzing(true);
    setError("");
    setNotice("");
    try {
      const response = await axios.post(
        apiUrl + "/moda/prestashop-alignment/runs/" + encodeURIComponent(runId) + "/reanalyze-blocked",
        { mappingOnly: true, limit: 100 },
        { headers },
      );
      const result = response?.data?.data || {};
      setNotice(
        number(result.reanalyzedItems) + " item ricalcolati; " +
        number(result.errorItems) + " errori.",
      );
      await load();
      if (onReanalyzed) await onReanalyzed(result);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
        requestError?.message ||
        "Ricalcolo degli item bloccati non completato.",
      );
    } finally {
      setReanalyzing(false);
    }
  };

  const kpis = dashboard?.kpis;
  const backlog = Array.isArray(dashboard?.mappingBacklog) ? dashboard.mappingBacklog : [];
  const fallback = Array.isArray(dashboard?.fallbackIt) ? dashboard.fallbackIt : [];

  return (
    <section className="alignment-dashboard pa3 mb3 sfondoBiancoTema br3" aria-labelledby="alignment-dashboard-title">
      <div className="alignment-dashboard-heading">
        <div>
          <h2 id="alignment-dashboard-title" className="f5 mt0 mb1">KPI e backlog mappature</h2>
          <p className="f7 mt0 mb0 scrittaColMedia">
            {runId ? "Dati della run selezionata." : "Dati complessivi delle lavorazioni."}
          </p>
        </div>
        {runId && canWrite ? (
          <button
            type="button"
            className="alignment-button alignment-button--secondary"
            onClick={reanalyzeBlocked}
            disabled={reanalyzing}
          >
            {reanalyzing ? "Ricalcolo in corso" : "Ricalcola item bloccati"}
          </button>
        ) : null}
      </div>

      {loading && !dashboard ? <DashboardSkeleton /> : null}
      {error ? <p role="alert" className="alignment-block-reason mb0 mt2">{error}</p> : null}
      {notice ? <p role="status" className="alignment-notice pa2 mb0 mt2">{notice}</p> : null}

      {kpis ? (
        <>
          <div className="alignment-kpi-grid mt3">
            <article><span>Run</span><strong>{number(kpis.runs?.total)}</strong><StatusBreakdown values={kpis.runs?.byStatus} /></article>
            <article><span>Articoli</span><strong>{number(kpis.items?.total)}</strong><StatusBreakdown values={kpis.items?.byStatus} /></article>
            <article><span>Azioni</span><strong>{number(kpis.actions?.total)}</strong><StatusBreakdown values={kpis.actions?.byStatus} /></article>
            <article><span>Lacune aperte</span><strong>{number(kpis.openFindings?.unique)}</strong><div className="f7 scrittaColMedia">{number(kpis.openFindings?.impactedItems)} articoli impattati</div></article>
          </div>
          <details className="mt2">
            <summary className="pointer f7">Azioni per dominio</summary>
            <div className="alignment-kpi-breakdown mt2">
              {Object.entries(kpis.actions?.byDomain || {}).map(([domain, count]) => (
                <span key={domain}>{DOMAIN_LABELS[domain] || domain} <strong>{number(count)}</strong></span>
              ))}
            </div>
          </details>
        </>
      ) : null}

      <div className="alignment-dashboard-section mt3">
        <div className="flex justify-between items-center mb2">
          <h3 className="f6 ma0">Backlog mapping</h3>
          <span className="f7 scrittaColMedia">{number(backlog.length)} lacune uniche</span>
        </div>
        {backlog.length ? (
          <div className="overflow-auto alignment-backlog-scroll" tabIndex="0" aria-label="Elenco backlog mapping">
            <table className="collapse w-100 f7 alignment-backlog-table">
              <thead><tr><th>Codice</th><th>Contesto</th><th>Dati richiesti</th><th>Impatto</th><th>Mapping</th></tr></thead>
              <tbody>{backlog.map((row, index) => (
                <tr key={[row.code, row.targetSite, row.fieldPath, row.language, index].join("-")}>
                  <td><strong>{row.code}</strong><div className="scrittaColMedia">{DOMAIN_LABELS[row.domain] || row.domain || "--"}</div></td>
                  <td>
                    <div>{row.sourceSite || "--"} → {row.targetSite || "--"}</div>
                    <div>{row.fieldPath || "--"}</div>
                    {row.language ? <div>Lingua: {row.language}</div> : null}
                    {row.categoryIds?.length ? <div>Categorie: {row.categoryIds.join(", ")}</div> : null}
                  </td>
                  <td>
                    {row.requirements?.length ? (
                      <ul className="ma0 pl3">
                        {row.requirements.map((requirement) => (
                          <li key={[requirement.source, requirement.field, requirement.path].join("-")}>
                            {requirementLabel(requirement)} <span className="scrittaColMedia">({requirement.source})</span>
                          </li>
                        ))}
                      </ul>
                    ) : <span className="scrittaColMedia">Nessun campo aggiuntivo dichiarato</span>}
                  </td>
                  <td><strong>{number(row.impactedItems)}</strong> articoli<div className="scrittaColMedia">{number(row.occurrences)} rilevazioni</div></td>
                  <td>
                    {row.mappingHref ? <Link className="alignment-mapping-link" to={row.mappingHref}>Apri pagina mapping</Link> : <span className="scrittaColMedia">Pagina non disponibile</span>}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : <p className="f7 scrittaColMedia mb0">Nessuna lacuna di mapping aperta nello scope corrente.</p>}
      </div>

      <div className="alignment-dashboard-section mt3">
        <div className="flex justify-between items-center mb2">
          <h3 className="f6 ma0">Fallback italiano attivo o proposto</h3>
          <span className="f7 scrittaColMedia">{number(fallback.length)} combinazioni campo/lingua</span>
        </div>
        {fallback.length ? (
          <div className="alignment-fallback-grid">
            {fallback.map((row) => (
              <article key={[row.targetSite, row.fieldPath, row.targetLanguage, row.fallbackSource].join("-")}>
                <strong>{semanticField(row.fieldPath)}</strong>
                <span>{row.targetSite || "--"} · IT → {String(row.targetLanguage || "--").toUpperCase()}</span>
                <span>{number(row.impactedItems)} articoli · {row.fallbackSource || "sorgente IT"}</span>
              </article>
            ))}
          </div>
        ) : <p className="f7 scrittaColMedia mb0">Nessun fallback IT attivo o proposto nello scope corrente.</p>}
      </div>
    </section>
  );
}
